// Turns "matched by ZIP" into "qualified / likely / long shot" using the five
// answers we collect in the Stack qualification follow-up.
//
// This is deliberately conservative: without the member's answers everything
// stays "matched", and nothing here is a promise of approval — it only reads
// the eligibility language a program already published.

export type EligibilityStatus = "qualified" | "likely" | "unlikely" | "unknown";

export interface QualificationProfile {
  income_range?: string | null;
  household_size_bucket?: string | null;
  is_first_time_buyer?: boolean | null;
  down_payment_savings?: string | null;
  purchase_timeline?: string | null;
}

export const hasQualificationData = (p: QualificationProfile | null | undefined): boolean =>
  Boolean(
    p &&
      (p.income_range ||
        p.household_size_bucket ||
        p.is_first_time_buyer !== null && p.is_first_time_buyer !== undefined ||
        p.down_payment_savings ||
        p.purchase_timeline),
  );

/** Midpoint of an income bucket, in dollars. */
export function incomeMidpoint(range?: string | null): number | null {
  if (!range) return null;
  const r = range.toLowerCase();
  if (r.includes("prefer not")) return null;
  if (r.includes("under $40")) return 30000;
  if (r.includes("$40k")) return 50000;
  if (r.includes("$60k")) return 70000;
  if (r.includes("$80k")) return 90000;
  if (r.includes("$100k")) return 125000;
  if (r.includes("$150k")) return 175000;
  return null;
}

export function householdSize(bucket?: string | null): number | null {
  if (!bucket) return null;
  if (/just me/i.test(bucket)) return 1;
  const n = parseInt(bucket, 10);
  return Number.isFinite(n) ? n : null;
}

/** Extract an income cap stated in a program's eligibility text, if any. */
export function statedIncomeCap(text?: string | null): number | null {
  if (!text) return null;
  const m = text.match(/\$\s?([\d,]{3,})/g);
  if (!m) return null;
  const looksLikeCap = /income|ami|earn|limit/i.test(text);
  if (!looksLikeCap) return null;
  const values = m
    .map((s) => parseInt(s.replace(/[^\d]/g, ""), 10))
    .filter((n) => n >= 20000 && n <= 400000);
  return values.length ? Math.max(...values) : null;
}

export interface EligibilityInput {
  eligibility_summary?: string | null;
  name?: string | null;
}

export interface EligibilityVerdict {
  status: EligibilityStatus;
  label: string;
  reason: string;
}

const VERDICTS: Record<EligibilityStatus, { label: string }> = {
  qualified: { label: "Looks like you qualify" },
  likely: { label: "Likely a fit" },
  unlikely: { label: "Check the fine print" },
  unknown: { label: "Matched" },
};

/**
 * Score one program against the member's answers.
 * Only downgrades on a rule the program itself states; never invents limits.
 */
export function scoreProgram(
  program: EligibilityInput,
  profile: QualificationProfile | null | undefined,
): EligibilityVerdict {
  if (!hasQualificationData(profile)) {
    return { status: "unknown", label: VERDICTS.unknown.label, reason: "Matched to your ZIP code." };
  }
  const p = profile!;
  const text = `${program.eligibility_summary ?? ""} ${program.name ?? ""}`;
  const reasons: string[] = [];
  let status: EligibilityStatus = "likely";

  // First-time buyer requirement
  const requiresFtb = /first[-\s]?time/i.test(text);
  if (requiresFtb) {
    if (p.is_first_time_buyer === true) {
      reasons.push("you're a first-time buyer");
      status = "qualified";
    } else if (p.is_first_time_buyer === false) {
      return {
        status: "unlikely",
        label: VERDICTS.unlikely.label,
        reason:
          "This one is written for first-time buyers. Some programs count you again after three years without owning — worth asking.",
      };
    }
  }

  // Income cap stated in the program text
  const cap = statedIncomeCap(program.eligibility_summary);
  const income = incomeMidpoint(p.income_range);
  if (cap && income) {
    if (income <= cap * 0.9) {
      reasons.push(`your income is comfortably under the ~$${cap.toLocaleString()} limit`);
      status = "qualified";
    } else if (income <= cap * 1.1) {
      reasons.push(`your income sits right at the ~$${cap.toLocaleString()} limit`);
      status = "likely";
    } else {
      return {
        status: "unlikely",
        label: VERDICTS.unlikely.label,
        reason: `Stated limit is around $${cap.toLocaleString()} and your range is above it. Limits often rise with household size — worth confirming.`,
      };
    }
  }

  // Household size can lift an income limit
  const size = householdSize(p.household_size_bucket);
  if (cap && size && size >= 3 && status !== "qualified") {
    reasons.push(`income limits usually rise for a household of ${size}`);
  }

  // Timeline is about urgency, not eligibility — surface it as context only.
  if (p.purchase_timeline === "0-3_months") reasons.push("you're buying within 3 months");

  return {
    status,
    label: VERDICTS[status].label,
    reason: reasons.length
      ? `Based on your answers: ${reasons.join(", ")}.`
      : "Nothing in your answers rules this out.",
  };
}

export const STATUS_CLASSES: Record<EligibilityStatus, string> = {
  qualified: "bg-emerald-100 text-emerald-800 border-emerald-200",
  likely: "bg-amber-100 text-amber-800 border-amber-200",
  unlikely: "bg-muted text-muted-foreground border-border",
  unknown: "bg-muted text-muted-foreground border-border",
};
